package ai.wagemix.web.screens.appshell

import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.appshell.WMAppShellPresenter
import ai.wagemix.logic.appshell.WMAppShellPresenter.Action
import ai.wagemix.logic.appshell.WMAppShellPresenter.Event
import ai.wagemix.logic.appshell.WMDefaultAppShellPresenter
import ai.wagemix.logic.appshell.WMPlanSummary
import ai.wagemix.net.models.WMSession
import ai.wagemix.net.models.WMStoredMix
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import com.fsryan.foundation.web.screens.FSWebViewModel
import com.fsryan.foundation.web.screens.FSWebViewStateHolder

interface WMAppShellWebViewStateHolder: FSWebViewStateHolder {
    /** `false` until the first session event; the chrome waits for it instead of guessing. */
    val sessionResolvedState: State<Boolean>
    val sessionState: State<WMSession?>
    val planState: State<WMPlanSummary?>
    val mixCountState: State<Int?>
    /** The user's most recently changed mixes, for the sidebar. */
    val recentMixesState: State<List<WMStoredMix>>
    val uploadCountState: State<Int?>
    val unsavedDraftState: State<Boolean>
}

abstract class WMBaseAppShellWebViewModel<
    E: Event,
    A: Action,
    PR: WMAppShellPresenter<E, A>
>(
    presenter: PR,
    protected val externalNavigation: (WMNavigationRequest) -> Unit
): FSWebViewModel<E, A, PR>(presenter), WMAppShellWebViewStateHolder {

    override val sessionResolvedState: State<Boolean> get() = _sessionResolvedState
    protected val _sessionResolvedState = mutableStateOf(false)

    override val sessionState: State<WMSession?> get() = _sessionState
    protected val _sessionState = mutableStateOf<WMSession?>(null)

    override val planState: State<WMPlanSummary?> get() = _planState
    protected val _planState = mutableStateOf<WMPlanSummary?>(null)

    override val mixCountState: State<Int?> get() = _mixCountState
    protected val _mixCountState = mutableStateOf<Int?>(null)

    override val recentMixesState: State<List<WMStoredMix>> get() = _recentMixesState
    protected val _recentMixesState = mutableStateOf<List<WMStoredMix>>(emptyList())

    override val uploadCountState: State<Int?> get() = _uploadCountState
    protected val _uploadCountState = mutableStateOf<Int?>(null)

    override val unsavedDraftState: State<Boolean> get() = _unsavedDraftState
    protected val _unsavedDraftState = mutableStateOf(false)

    override fun customEventHandling(event: E) {
        super.customEventHandling(event)
        with(event) {
            sessionUpdate?.let { _sessionState.value = it.value; _sessionResolvedState.value = true }
            planUpdate?.let { _planState.value = it }
            mixCountUpdate?.let { _mixCountState.value = it }
            recentMixesUpdate?.let { _recentMixesState.value = it }
            uploadCountUpdate?.let { _uploadCountState.value = it }
            unsavedDraftUpdate?.let { _unsavedDraftState.value = it }
            navigationUpdate?.let { externalNavigation(it) }
        }
    }

    fun userRequestedSignOut(extendedProperties: Map<String, Any> = emptyMap()) = presenter.userRequestedSignOut(extendedProperties)

    fun userRequestedStartNewMix(extendedProperties: Map<String, Any> = emptyMap()) {
        presenter.userRequestedStartNewMix(extendedProperties)
    }

    fun userRequestedOpenMix(mixId: String, extendedProperties: Map<String, Any> = emptyMap()) {
        presenter.userRequestedOpenMix(mixId, extendedProperties)
    }
}

typealias WMDefaultAppShellWebViewModel = WMBaseAppShellWebViewModel<Event.Base, Action.Base, WMDefaultAppShellPresenter>

fun createDefaultAppShellWebViewModel(
    presenter: WMDefaultAppShellPresenter,
    externalNavigation: (WMNavigationRequest) -> Unit
): WMDefaultAppShellWebViewModel = object: WMDefaultAppShellWebViewModel(presenter, externalNavigation) {}
